import type { StandardReportColumnType } from '@/types/standardReports.types';

/**
 * The single value formatter for standard reports.
 *
 * ONE function, switched on the column's declared type — never a per-report
 * branch. This is the mechanism that makes "a new report needs a definition and
 * a transform, and no frontend change" true: a report the frontend has never
 * heard of renders correctly because its columns declare what they are.
 */

/**
 * Formats money WITHOUT parsing it into a number.
 *
 * The value arrives as an exact decimal string, cast in SQL, precisely because
 * the underlying column is a float and summing it drifts. `parseFloat` here
 * would reintroduce that drift at the last possible moment, in the number a
 * reader actually looks at. So this groups the integer part textually and
 * leaves the decimals exactly as they arrived.
 */
export function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';

  const raw = String(value).trim();
  const negative = raw.startsWith('-');
  const unsigned = negative ? raw.slice(1) : raw;

  if (!/^\d+(\.\d+)?$/.test(unsigned)) return raw;

  const [whole, decimals = ''] = unsigned.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const cents = decimals.padEnd(2, '0').slice(0, 2);

  return `${negative ? '-' : ''}$${grouped}.${cents}`;
}

/**
 * Dates arrive already rendered in Pacific by the server and are shown as-is.
 *
 * Constructing a `Date` here would reinterpret that string in the viewer's
 * timezone and shift a late-evening Pacific timestamp onto the next day — so
 * the screen would disagree with the workbook for the same run.
 */
export function formatReportDate(value: unknown): string {
  if (value === null || value === undefined) return '';

  const raw = String(value).trim();
  if (raw === '') return '';

  // The legacy zero-date sentinel means "absent", not "year zero".
  if (raw.startsWith('0000-00-00')) return '';

  return raw;
}

export function formatInt(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';

  const raw = String(value).trim();
  if (!/^-?\d+$/.test(raw)) return raw;

  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatValue(value: unknown, type: StandardReportColumnType): string {
  switch (type) {
    case 'money':
      return formatMoney(value as string);
    case 'date':
      return formatReportDate(value);
    case 'int':
      return formatInt(value);
    case 'text':
    default:
      return value === null || value === undefined ? '' : String(value);
  }
}

/** Right-align the numeric columns, as a spreadsheet would. */
export function alignmentFor(type: StandardReportColumnType): 'left' | 'right' {
  return type === 'money' || type === 'int' ? 'right' : 'left';
}
